import React, { createContext, useCallback, useContext, useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import { If } from 'utils';
import { UiKit } from 'components';
import * as Feature from './features';
import { FTUCompleted } from './components';
import { useStoreOnboarding } from '../context';
import { useToggle, useSegment, useRetailer } from 'utils/hooks';
import {
  canGoBack,
  getActiveFeature,
  getActiveStep,
  getNextStepAndFeature,
  getOnboardingStatus,
  getPreviousStepAndFeature
} from './utils';
import { ReactComponent as LeftArrowIcon } from 'assets/ss-icons/left-arrow.svg';
import { IS_ALL_SSR } from 'dictionary';

/**
 * @type {React.Context<{
 *  step: keyof typeof import("../stubs").STEP_ID,
 *  onToggle: () => void,
 *  onGotoNext: (err: Error | null) => void
 * }>}
 */
export const FTUWizardContext = createContext();

export function useFTUWizard() {
  const context = useContext(FTUWizardContext);
  if (!context) {
    throw new Error('useFTUWizard must be used within a FTUWizardProvider');
  }
  return context;
}

export function FTUWizardProvider({
  triggerIfPending = false,
  children = null
}) {
  const { isAdvancedMode, getStoreProperty } = useRetailer();
  const { todos, steps, onMarkTodoDone } = useStoreOnboarding();
  const { track } = useSegment();

  const isAdvancedSupported = getStoreProperty('is_enhanced_supported', false);
  const { isCompleted } = getOnboardingStatus(
    todos,
    isAdvancedMode,
    isAdvancedSupported
  );

  const [isOpen, toggleWizard] = useToggle(triggerIfPending && !isCompleted);
  const [step, setStep] = useState(() =>
    getActiveStep(todos, isAdvancedMode, isAdvancedSupported)
  );
  const [feature, setFeature] = useState(() =>
    getActiveFeature(step, todos, isAdvancedMode, isAdvancedSupported)
  );

  const isBackAllowed = canGoBack(feature, isAdvancedMode, isAdvancedSupported);

  const onToggle = useCallback(() => {
    const willBeOpen = !isOpen;

    if (willBeOpen) {
      track('FTU Wizard Opened', {
        current_step: step,
        current_feature: feature
      });
    } else {
      track('FTU Wizard Closed', {
        current_step: step,
        current_feature: feature
      });
    }

    toggleWizard();
  }, [isOpen, step, feature, track, toggleWizard]);

  const onClosed = useCallback(() => {
    const step = getActiveStep(todos, isAdvancedMode, isAdvancedSupported);
    const feature = getActiveFeature(
      step,
      todos,
      isAdvancedMode,
      isAdvancedSupported
    );
    setStep(step);
    setFeature(feature);
  }, [todos, isAdvancedMode, isAdvancedSupported]);

  const onGotoNext = useCallback(
    (err = null) => {
      if (err) return;
      const { stepId, featureId } = getNextStepAndFeature(
        step,
        feature,
        isAdvancedMode,
        isAdvancedSupported
      );

      // Track wizard completion
      if (featureId === 'START') {
        track('FTU Wizard Started', {
          first_step: step,
          first_feature: featureId
        });
      } else if (featureId === 'COMPLETED') {
        track('FTU Wizard Completed', {
          final_step: step,
          final_feature: feature
        });
      } else {
        // Track feature completion
        track('FTU Wizard Feature Completed', {
          step,
          feature,
          next_step: stepId,
          next_feature: featureId
        });
      }

      onMarkTodoDone(feature);
      setStep(stepId);
      setFeature(featureId);
    },
    [step, feature, isAdvancedMode, isAdvancedSupported, onMarkTodoDone, track]
  );

  const onGoBack = useCallback(() => {
    const { stepId, featureId } = getPreviousStepAndFeature(
      step,
      feature,
      isAdvancedMode,
      isAdvancedSupported
    );

    track('FTU Wizard Back Clicked', {
      from_step: step,
      from_feature: feature,
      to_step: stepId,
      to_feature: featureId
    });

    setStep(stepId);
    setFeature(featureId);
  }, [step, feature, isAdvancedMode, isAdvancedSupported, track]);

  return (
    <FTUWizardContext.Provider value={{ step, onToggle, onGotoNext }}>
      {children}
      <UiKit.Sheet
        isOpen={isOpen}
        onClose={onToggle}
        onClosed={onClosed}
        wrapClassName={styles.sheetWrapper}
        modalClassName={cn(styles.sheet, { [styles.isAllSSR]: IS_ALL_SSR })}
        contentClassName={styles.sheetContent}
      >
        <If test={!IS_ALL_SSR}>
          <UiKit.Sheet.Header title="Return Policy Setup" />
        </If>
        <UiKit.Sheet.Body className="p-0">
          <UiKit.Grid
            templateColumns="auto 1fr"
            className="h-100 overflow-hidden"
          >
            <div className={styles.sidebar}>
              <If test={step !== 'START'}>
                <UiKit.Stepper
                  mode="dark"
                  variant="number"
                  steps={steps}
                  value={step}
                  className={styles.stepper}
                />
              </If>
            </div>
            <div className={styles.content}>
              <div className={styles.body}>
                {feature === 'START' && <Feature.ReturnsMode />}
                {feature === 'RETURN_REASONS' && <Feature.RequestReasons />}
                {feature === 'RETURN_WINDOW' && <Feature.ReturnWindow />}
                {feature === 'REFUND_METHODS' && <Feature.RefundMethods />}
                {feature === 'EXCHANGES' && <Feature.Exchanges />}
                {feature === 'SHIPPING' && <Feature.Shipping />}
                {feature === 'FEES' && <Feature.Fees />}
                {feature === 'COMPLETED' && <FTUCompleted />}
              </div>
            </div>
          </UiKit.Grid>
        </UiKit.Sheet.Body>
        <UiKit.Sheet.Footer hideCancelBtn>
          <If test={isBackAllowed}>
            <UiKit.Button size="lg" variant="ghost" onClick={onGoBack}>
              <LeftArrowIcon />
              Back
            </UiKit.Button>
          </If>
        </UiKit.Sheet.Footer>
      </UiKit.Sheet>
    </FTUWizardContext.Provider>
  );
}
